import {MediaClip, ClipFadeConfig, ClipTransitionPoint} from "../media";
import {Track} from "../../tracks";
import {MediaSourceRef} from "../../sources/media-source";
import {VideoClipProps} from "./video.interfaces";
import { applyShapeMask, type MaskShape } from "../../utils/shapeMask";
import {VisualMixin, visualize} from "../mixins";


// mediabunny pieces
import {AudioBufferSink, CanvasSink, Input, WrappedAudioBuffer, WrappedCanvas, UrlSource, ALL_FORMATS} from 'mediabunny';
import {Timestamp} from "../../models";
import {AudioMixer, MixerTrackHandle} from "../../media/audio-mixer";
import {AudioClip} from "../audio";
import {IOError} from "../../errors";
import {Sprite, Texture} from "pixi.js";

const HALF_PI = Math.PI / 2;

/**
 * Per-clip peak limiter state (persists across chunks for smooth envelope)
 */
type LimiterState = {
    envelope: number;  // Current envelope level (0-1)
};

/**
 * Apply fade envelope to audio samples in-place using equal-power curves.
 * Equal-power uses sin/cos curves which maintain constant perceived loudness.
 */
function applyFadeEnvelope(
    planes: Float32Array[],
    sampleRate: number,
    chunkStartSrcSec: number,
    chunkDurationSrcSec: number,
    clipStartSrcSec: number,
    clipEndSrcSec: number,
    playbackRate: number,
    fadeIn?: ClipFadeConfig,
    fadeOut?: ClipFadeConfig,
    transitionPoints?: ClipTransitionPoint[]
): void {
    const numFrames = planes[0]?.length ?? 0;
    if (numFrames === 0) return;
    
    if (!Number.isFinite(clipStartSrcSec) || !Number.isFinite(clipEndSrcSec)) return;
    if (clipEndSrcSec <= clipStartSrcSec) return;
    const rate = Number.isFinite(playbackRate) && playbackRate > 0 ? playbackRate : 1;

    const fadeInDurationSrcSec = ((fadeIn?.durationMs ?? 0) / 1000) * rate;
    const fadeOutDurationSrcSec = ((fadeOut?.durationMs ?? 0) / 1000) * rate;
    const fadeInEndSrcSec = clipStartSrcSec + fadeInDurationSrcSec;
    const fadeOutStartSrcSec = clipEndSrcSec - fadeOutDurationSrcSec;
    
    const chunkEndSrcSec = chunkStartSrcSec + chunkDurationSrcSec;
    
    // Check if any fades are configured
    const hasEdgeFades = fadeInDurationSrcSec > 0 || fadeOutDurationSrcSec > 0;
    const hasTransitionFades = transitionPoints && transitionPoints.some(tp => 
        tp.transition?.fadeIn?.durationMs || tp.transition?.fadeOut?.durationMs
    );
    
    if (!hasEdgeFades && !hasTransitionFades) return;
    
    const overlapsEdgeIn =
        fadeInDurationSrcSec > 0 &&
        chunkStartSrcSec < fadeInEndSrcSec &&
        chunkEndSrcSec > clipStartSrcSec;
    const overlapsEdgeOut =
        fadeOutDurationSrcSec > 0 &&
        chunkStartSrcSec < clipEndSrcSec &&
        chunkEndSrcSec > fadeOutStartSrcSec;
    
    // Build list of active transitions for this chunk
    const activeTransitions: Array<{
        pointSec: number;
        fadeOutDurationSrcSec: number;
        fadeInDurationSrcSec: number;
    }> = [];
    
    if (transitionPoints) {
        for (const tp of transitionPoints) {
            const tpFadeOutDurSrcSec = ((tp.transition?.fadeOut?.durationMs ?? 0) / 1000) * rate;
            const tpFadeInDurSrcSec = ((tp.transition?.fadeIn?.durationMs ?? 0) / 1000) * rate;
            if (tpFadeOutDurSrcSec <= 0 && tpFadeInDurSrcSec <= 0) continue;
            
            const pointSec = tp.srcTimeSec;
            if (!Number.isFinite(pointSec)) continue;
            if (pointSec < clipStartSrcSec || pointSec > clipEndSrcSec) continue;

            const fadeOutStart = pointSec - tpFadeOutDurSrcSec;
            const fadeInEnd = pointSec + tpFadeInDurSrcSec;
            
            if (chunkEndSrcSec > fadeOutStart && chunkStartSrcSec < fadeInEnd) {
                activeTransitions.push({
                    pointSec,
                    fadeOutDurationSrcSec: tpFadeOutDurSrcSec,
                    fadeInDurationSrcSec: tpFadeInDurSrcSec,
                });
            }
        }
    }
    
    if (!overlapsEdgeIn && !overlapsEdgeOut && activeTransitions.length === 0) return;
    
    for (let i = 0; i < numFrames; i++) {
        const sampleTimeSec = chunkStartSrcSec + (i / sampleRate);
        let gain = 1.0;
        
        // Clip edge fade-in
        if (fadeInDurationSrcSec > 0 && sampleTimeSec >= clipStartSrcSec && sampleTimeSec < fadeInEndSrcSec) {
            const t = (sampleTimeSec - clipStartSrcSec) / fadeInDurationSrcSec;
            gain *= Math.sin(t * HALF_PI);
        }
        
        // Clip edge fade-out
        if (fadeOutDurationSrcSec > 0 && sampleTimeSec >= fadeOutStartSrcSec && sampleTimeSec < clipEndSrcSec) {
            const t = (sampleTimeSec - fadeOutStartSrcSec) / fadeOutDurationSrcSec;
            gain *= Math.cos(t * HALF_PI);
        }
        
        // Transition point fades
        for (const tp of activeTransitions) {
            if (tp.fadeOutDurationSrcSec > 0) {
                const fadeOutStart = tp.pointSec - tp.fadeOutDurationSrcSec;
                if (sampleTimeSec >= fadeOutStart && sampleTimeSec < tp.pointSec) {
                    const t = (sampleTimeSec - fadeOutStart) / tp.fadeOutDurationSrcSec;
                    gain *= Math.cos(t * HALF_PI);
                }
            }
            if (tp.fadeInDurationSrcSec > 0) {
                const fadeInEnd = tp.pointSec + tp.fadeInDurationSrcSec;
                if (sampleTimeSec >= tp.pointSec && sampleTimeSec < fadeInEnd) {
                    const t = (sampleTimeSec - tp.pointSec) / tp.fadeInDurationSrcSec;
                    gain *= Math.sin(t * HALF_PI);
                }
            }
        }
        
        for (const plane of planes) {
            plane[i] *= gain;
        }
    }
}

export class VideoClip extends VisualMixin(MediaClip<VideoClipProps>) {
    public readonly type = 'video';
    public track?: Track<VideoClip>;

    // --- media source (contains both video and audio tracks) ---
    public videoSource!: MediaSourceRef;

    // --- playback state ---
    public playing = false;

    // === VIDEO DECODING ===
    private _input?: Input;
    private _videoSink?: CanvasSink;
    private _videoIter?: AsyncGenerator<WrappedCanvas | null, void, unknown>;
    private _nextFrame: WrappedCanvas | null = null;
    private _lastVideoDecodeErrorAtMs = 0;

    // cache for upload
    private _nextFrameCanvas?: HTMLCanvasElement;
    private _lastW = 0;
    private _lastH = 0;

    // === CROP ===
    // Initialize to full video (no crop) so getCrop() always returns a valid value
    private _crop: { x: number; y: number; width: number; height: number } = { x: 0, y: 0, width: 1, height: 1 };
    // Original source dimensions (before any crop)
    private _originalSourceWidth?: number;
    private _originalSourceHeight?: number;

    // === AUDIO DECODING ===
    private _audioSink?: AudioBufferSink;
    private _audioIter?: AsyncGenerator<WrappedAudioBuffer, void, unknown>;
    private _mixerTrack?: MixerTrackHandle;
    private _feederRunning = false;
    private _audioPrimeSeq = 0;
    private _sample_rate?: number;

    // Studio Sound state (for audio replacement)
    private _studioSoundEnabled = false;
    private _originalAudioUrl?: string;
    private _denoisedAudioUrl?: string;
    private _customAudioInput?: Input; // Separate input for replaced audio
    private _limiterState: LimiterState = { envelope: 0 };

    public mixerTrack(): MixerTrackHandle | undefined {
        return this._mixerTrack;
    }

    private readonly canvas = document.createElement('canvas');
    private readonly context = this.canvas.getContext('2d')!;

    public readonly textures = {
        canvas: Texture.from(this.canvas),
    };

    // Visual sprite that Pixi will render
    private readonly sprite = new Sprite(this.textures.canvas);

    // Normalize OffscreenCanvas to HTMLCanvasElement for drawing/returns
    private toHTMLCanvas(src: HTMLCanvasElement | OffscreenCanvas): HTMLCanvasElement {
        if (src instanceof HTMLCanvasElement) return src;
        const c = document.createElement('canvas');
        c.width = src.width;
        c.height = src.height;
        const ctx = c.getContext('2d')!;
        // drawImage accepts OffscreenCanvas in modern browsers
        ctx.drawImage(src as any, 0, 0);
        return c;
    }

    private handleVideoDecodeError(error: unknown): void {
        const now = Date.now();
        if (now - this._lastVideoDecodeErrorAtMs > 2000) {
            this._lastVideoDecodeErrorAtMs = now;
            // Avoid spamming the console; decoding errors can occur on some sources/timestamps.
            console.warn("[VideoClip] Video decode error; resetting iterator", {
                clipId: (this as any)?.id,
                error,
            });
        }

        try { (this._videoIter as any)?.return?.(); } catch {}
        this._videoIter = undefined;
        try { (this._nextFrame as any)?.close?.(); } catch {}
        this._nextFrame = null;
    }

    public constructor(
        videoSource: MediaSourceRef,
        props: VideoClipProps = {},
        id?: string,
    ) {
        super();
        this.videoSource = videoSource;

        if (id) {
            this.id = id;
        }

        // if (props.playbackRate !== undefined) this.playbackRate = props.playbackRate;
        if (props.gain !== undefined) this.gain = props.gain;

        this.props = props;
        Object.assign(this, props);

        // Add the sprite to this clip's Pixi container so it becomes visible
        this.sprite.anchor.set(0.5, 0.5);
        this.sprite.position.set(0, 0);
        this.view.addChild(this.sprite);
        this.init().then(() => {});
    }

    public async init(): Promise<void> {
        if (!this.videoSource) {
            this.state = 'ERROR';
            return;
        }

        try {
            const input = this.videoSource.input;
            
            // Check video track
            const vTrack = await input.getPrimaryVideoTrack();
            if (!vTrack || vTrack.codec === null || !(await vTrack.canDecode())) {
                this.state = 'ERROR';
                throw new IOError({ code: 'unsupportedVideoCodec', message: 'Unsupported/undecodable video codec.' });
            }
            this.duration.seconds = await vTrack.computeDuration();

            // Check audio track (same source)
            const aTrack = await input.getPrimaryAudioTrack();
            if (aTrack && (await aTrack.canDecode())) {
                this.hasAudioStream = true;
                this._audioSink = new AudioBufferSink(aTrack);
                this._sample_rate = aTrack.sampleRate;
            }
        } catch (e) {
            this.state = 'ERROR';
        }
    }

    /**
     * Prepare sinks/iterators for both audio & video.
     * Called from Composition.play() before starting transport.
     */
    public async prepareForPlayback(
        audioCtx: AudioContext,
        masterGain: GainNode,  // you may or may not use this now
        mixer: AudioMixer
    ): Promise<void> {
        // 1) Ensure we have an Input
        if (!this._input) {
            this._input = this.videoSource.input;
        }

        // 2. Pull primary video track -> CanvasSink
        const vTrack = await this._input.getPrimaryVideoTrack();
        if (vTrack && (await vTrack.canDecode())) {
            const canBeTransparent = await vTrack.canBeTransparent();
            if (!this._videoSink) {
                this._videoSink = new CanvasSink(vTrack, {
                    poolSize: 2,
                    fit: 'contain',        // you can refine this based on props.position/width/etc.
                    alpha: canBeTransparent,
                });
            }
            try {
                const dur = await vTrack.computeDuration();
                if (Number.isFinite(dur) && dur > 0) this.duration.seconds = dur;
            } catch { /* ignore */ }
        } else {
            this._videoSink = undefined;
        }

        // 3. Pull audio track -> AudioBufferSink
        // If _audioSink already exists (e.g., from replaceAudioSource for Studio Sound),
        // don't overwrite it - use the existing one with its correct sample rate.
        // Only create from original video if no custom audio source was set.
        const aTrack = await this._input.getPrimaryAudioTrack();
        if (this._audioSink || (aTrack && (await aTrack.canDecode()))) {
            // Only create sink from original video if we don't have a custom one
            if (!this._audioSink && aTrack) {
                this._audioSink = new AudioBufferSink(aTrack);
                this._sample_rate = aTrack.sampleRate;
            }
            
            // Register with the mixer if not already registered
            // Use stored _sample_rate (which may be from replaced audio source)
            if (!this._mixerTrack) {
                const sampleRate = this._sample_rate ?? aTrack?.sampleRate ?? 48000;
                const channels = aTrack?.numberOfChannels ?? 2;
                
                this._mixerTrack = mixer.registerTrack({
                    clipId: this.getClipId(),
                    channels,
                    sampleRate,
                });
                
                console.log('[VideoClip.prepareForPlayback] Registered mixer track with sampleRate:', sampleRate);
            }
            
            this.hasAudioStream = true;
            // Fallback duration from audio if video didn't provide it
            if (!(this.duration.seconds > 0) && aTrack) {
                try {
                    const audioDur = await aTrack.computeDuration();
                    if (Number.isFinite(audioDur) && audioDur > 0) this.duration.seconds = audioDur;
                } catch { /* ignore */ }
            }
        } else {
            this._audioSink = undefined;
            this.hasAudioStream = false;
        }

        // Set READY if we got at least video or audio
        if (this._videoSink || this._audioSink) {
            this.state = 'READY';
        } else {
            this.state = 'ERROR';
        }
    }

    /**
     * Called by Composition.play() and Composition.seek()
     * to ensure audio is primed at a given comp time.
     * Mirrors AudioClip.primeAudio().
     */
    public async primeAudio(compTimeSec: number, mixer: AudioMixer, prebufferSec = 0.2) {
        if (!this._audioSink || !this._mixerTrack) return;

        this._audioPrimeSeq += 1;
        const primeSeq = this._audioPrimeSeq;

        const clipLocal = this.CompositionTimeToClipTime(compTimeSec);

        // Reset limiter state on seek/new playback to avoid artifacts
        this._limiterState.envelope = 0;

        const MAX_BUFFER_AHEAD_SEC = 15;
        const BACKPRESSURE_SLEEP_MS = 25;
        const EPS = 1e-3;
        const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

        // Dispose any previous iterator
        await (this._audioIter as any)?.return?.();
        mixer.flushTrack(this._mixerTrack, clipLocal);

        const effectiveSampleRate = this._sample_rate || 48000;
        let acc = 0;

        const segsRaw: Array<{ srcStartSec: number; srcEndSec: number; compStartSec: number }> =
            Array.isArray((this as any).segments) ? ((this as any).segments as any) : [];
        const clipStartSrcSec =
            Array.isArray(segsRaw) && segsRaw.length > 0
                ? Number(segsRaw[0].srcStartSec ?? (this.range?.[0]?.seconds ?? 0))
                : Number(this.range?.[0]?.seconds ?? 0);
        const clipEndSrcSec =
            Array.isArray(segsRaw) && segsRaw.length > 0
                ? Number(segsRaw[segsRaw.length - 1].srcEndSec ?? (this.range?.[1]?.seconds ?? clipStartSrcSec))
                : Number(this.range?.[1]?.seconds ?? clipStartSrcSec);
        const playbackRate = Number(this.playbackRate ?? 1);

        const fadeIn = this.fadeIn;
        const fadeOut = this.fadeOut;
        const transitionPts = this.transitionPoints;

        const segs: Array<{ srcStartSec: number; srcEndSec: number; compStartSec: number }> =
            Array.isArray(segsRaw) && segsRaw.length > 0
                ? segsRaw
                : [
                      {
                          srcStartSec: clipStartSrcSec,
                          srcEndSec: clipEndSrcSec,
                          compStartSec: this.start?.seconds ?? 0,
                      },
                  ];

        let segIdx = segs.findIndex((s) => clipLocal >= s.srcStartSec && clipLocal < s.srcEndSec);
        if (segIdx < 0) segIdx = segs.findIndex((s) => clipLocal < s.srcEndSec);
        if (segIdx < 0) return;

        let seg = segs[segIdx];

        // Track both source-time and the corresponding composition-time buffer frontier.
        let nextChunkTimeSec = clipLocal;
        let nextChunkCompTimeSec = seg.compStartSec + Math.max(0, nextChunkTimeSec - seg.srcStartSec);

        let iter: AsyncGenerator<WrappedAudioBuffer, void, unknown> = this._audioSink.buffers(
            nextChunkTimeSec,
            seg.srcEndSec
        );
        this._audioIter = iter;

        const advanceSegment = async (): Promise<boolean> => {
            if (this._audioPrimeSeq !== primeSeq) return false;
            segIdx += 1;
            if (segIdx >= segs.length) return false;
            seg = segs[segIdx];
            nextChunkTimeSec = seg.srcStartSec;
            nextChunkCompTimeSec = seg.compStartSec;
            try {
                await (iter as any)?.return?.();
            } catch {}
            iter = this._audioSink!.buffers(nextChunkTimeSec, seg.srcEndSec);
            this._audioIter = iter;
            return true;
        };

        const readNextBuffer = async (): Promise<AudioBuffer | null> => {
            while (true) {
                if (this._audioPrimeSeq !== primeSeq) return null;
                const n = await iter.next();
                if (this._audioPrimeSeq !== primeSeq) return null;
                if (!n || n.done || !n.value || !n.value.buffer) {
                    const ok = await advanceSegment();
                    if (!ok) return null;
                    continue;
                }
                return n.value.buffer;
            }
        };

        const writeProcessedBuffer = (buffer: AudioBuffer): number => {
            const timestamp = nextChunkTimeSec;
            const segRemainingSec = Math.max(0, seg.srcEndSec - nextChunkTimeSec);
            if (segRemainingSec <= EPS) return 0;

            let frames = buffer.length;
            let durationSec = frames / effectiveSampleRate;
            if (durationSec > segRemainingSec + EPS) {
                frames = Math.max(0, Math.floor(segRemainingSec * effectiveSampleRate));
                durationSec = frames / effectiveSampleRate;
                if (frames <= 0) return 0;
            }

            const channels = buffer.numberOfChannels;
            let planes: Float32Array[] = [];
            for (let ch = 0; ch < channels; ch++) {
                planes.push(buffer.getChannelData(ch).slice());
            }
            if (frames !== buffer.length) {
                planes = planes.map((p) => p.slice(0, frames));
            }

            // Apply fade envelope (clip edges + transition points)
            applyFadeEnvelope(
                planes,
                effectiveSampleRate,
                nextChunkTimeSec,
                durationSec,
                clipStartSrcSec,
                clipEndSrcSec,
                playbackRate,
                fadeIn,
                fadeOut,
                transitionPts
            );

            nextChunkTimeSec += durationSec;
            nextChunkCompTimeSec += durationSec;

            mixer.writePcm(this._mixerTrack!, { timestamp, frames, channels, planes });
            return durationSec;
        };

        // Prebuffer loop
        while (acc < prebufferSec) {
            if (this._audioPrimeSeq !== primeSeq) return;
            const buf = await readNextBuffer();
            if (!buf) break;
            const wroteSec = writeProcessedBuffer(buf);
            acc += wroteSec;
            if (seg.srcEndSec - nextChunkTimeSec <= EPS) {
                const ok = await advanceSegment();
                if (!ok) break;
            }
        }

        // Continuous feeder - capture configs for the async loop
        const capturedFadeIn = fadeIn;
        const capturedFadeOut = fadeOut;
        const capturedTransitionPts = transitionPts;
        const capturedStudioSound = this._studioSoundEnabled;
        const limiterState = this._limiterState;  // Reference to mutable state object
        const capturedClipStartSrcSec = clipStartSrcSec;
        const capturedClipEndSrcSec = clipEndSrcSec;
        const capturedPlaybackRate = playbackRate;

        (async () => {
            while (true) {
                if (this._audioPrimeSeq !== primeSeq) return;

                // Backpressure: don't decode far ahead of playhead (composition time).
                const comp = this.track?.composition as any;
                const playheadSec =
                    comp && typeof comp.getPlayheadTimeSec === "function"
                        ? Number(comp.getPlayheadTimeSec())
                        : Number(compTimeSec ?? 0);
                const bufferAhead = nextChunkCompTimeSec - playheadSec;
                if (Number.isFinite(bufferAhead) && bufferAhead > MAX_BUFFER_AHEAD_SEC) {
                    await sleep(BACKPRESSURE_SLEEP_MS);
                    continue;
                }

                const buf = await readNextBuffer();
                if (!buf) return;

                const timestamp = nextChunkTimeSec;
                const segRemainingSec = Math.max(0, seg.srcEndSec - nextChunkTimeSec);
                if (segRemainingSec <= EPS) {
                    const ok = await advanceSegment();
                    if (!ok) return;
                    continue;
                }

                const channels = buf.numberOfChannels;
                let frames = buf.length;
                let durationSec = frames / effectiveSampleRate;
                if (durationSec > segRemainingSec + EPS) {
                    frames = Math.max(0, Math.floor(segRemainingSec * effectiveSampleRate));
                    durationSec = frames / effectiveSampleRate;
                    if (frames <= 0) {
                        const ok = await advanceSegment();
                        if (!ok) return;
                        continue;
                    }
                }

                let planes: Float32Array[] = [];
                for (let ch = 0; ch < channels; ch++) {
                    planes.push(buf.getChannelData(ch).slice());
                }
                if (frames !== buf.length) {
                    planes = planes.map((p) => p.slice(0, frames));
                }

                // Apply fade envelope (clip edges + transition points)
                applyFadeEnvelope(
                    planes,
                    effectiveSampleRate,
                    nextChunkTimeSec,
                    durationSec,
                    capturedClipStartSrcSec,
                    capturedClipEndSrcSec,
                    capturedPlaybackRate,
                    capturedFadeIn,
                    capturedFadeOut,
                    capturedTransitionPts
                );

                nextChunkTimeSec += durationSec;
                nextChunkCompTimeSec += durationSec;

                if (this._audioPrimeSeq !== primeSeq) return;
                mixer.writePcm(this._mixerTrack!, { timestamp, frames, channels, planes });

                if (seg.srcEndSec - nextChunkTimeSec <= EPS) {
                    const ok = await advanceSegment();
                    if (!ok) return;
                }
            }
        })().catch(() => {});
    }

    /**
     * Stop pushing audio (called on pause()).
     */
    public stopAudio(): void {
        this._feederRunning = false;
        this._audioPrimeSeq += 1;
        if (this._audioIter && (this._audioIter as any).return) {
            try { (this._audioIter as any).return(); } catch {}
        }
        this._audioIter = undefined;
    }

    /**
     * Internal helper to lazily fill _nextFrame.
     * Pull frames from _videoIter until we get a frame in the future.
     */
    private async _updateNextFrame(targetCompSec: number): Promise<void> {
        const iter = this._videoIter;
        if (!iter) return;
        const targetLocal = this.CompositionTimeToClipTime(targetCompSec);
        while (true) {
            let r: IteratorResult<WrappedCanvas | null, void>;
            try {
                r = await iter.next();
            } catch {
                // Iterator errored or was invalidated; clear state if it's still ours.
                if (this._videoIter === iter) {
                    try { (iter as any)?.return?.(); } catch {}
                    this._videoIter = undefined;
                }
                try { (this._nextFrame as any)?.close?.(); } catch {}
                this._nextFrame = null;
                return;
            }

            // If the iterator has been replaced (seek/crop), ignore stale frames.
            if (this._videoIter !== iter) {
                try { (r as any)?.value?.close?.(); } catch {}
                return;
            }

            const candidate = (r.value ?? null) as WrappedCanvas | null;
            if (!candidate) {
                // no more frames. Attempt close of frame
                try { (this._nextFrame as any)?.close?.(); } catch {}
                this._nextFrame = null;
                return;
            }

            // candidate.timestamp is in *source* time for this clip
            // Cache the first frame that is > target local time; otherwise close and keep catching up.
            if (candidate.timestamp > targetLocal) {
                try { (this._nextFrame as any)?.close?.(); } catch {}
                this._nextFrame = candidate;
                return;
            }

            // Frame is "in the past"; close it and continue.
            try { (candidate as any)?.close?.(); } catch {}
        }
    }

    // Warm the video iterator at a given composition time so the first frame is ready when we enter
    public async warmVideoAt(compTimeSec: number): Promise<void> {
        if (!this._videoSink) return;
        const clipLocalT = this.CompositionTimeToClipTime(compTimeSec);
        let iter: AsyncGenerator<WrappedCanvas, void, unknown>;
        try {
            iter = this._videoSink.canvases(clipLocalT);
            this._videoIter = iter;
        } catch (e) {
            this.handleVideoDecodeError(e);
            return;
        }

        let first: WrappedCanvas | null = null;
        let second: WrappedCanvas | null = null;
        try {
            const r1 = await iter.next();
            if (!r1.done) first = r1.value as WrappedCanvas;
            const r2 = await iter.next();
            if (!r2.done) second = r2.value as WrappedCanvas;
        } catch (e) {
            if (this._videoIter === iter) {
                this.handleVideoDecodeError(e);
            }
            try { (first as any)?.close?.(); } catch {}
            try { (second as any)?.close?.(); } catch {}
            return;
        }
        if (this._videoIter !== iter) {
            try { (first as any)?.close?.(); } catch {}
            try { (second as any)?.close?.(); } catch {}
            return;
        }
        try { (this._nextFrame as any)?.close?.(); } catch {}
        if (second) {
            try { (first as any)?.close?.(); } catch {}
        }
        this._nextFrame = (second ?? first ?? null) as WrappedCanvas | null;
    }

    /**
     * Return a canvas for the video frame we should show at compTimeSec, or null if not ready.
     * This logic mirrors the mediabunny demo:
     * - if the queued next frame is now due, use it and queue another
     * - else, if we have nothing queued yet, start queuing
     */
    public async pullVideoFrame(compTimeSec: number): Promise<HTMLCanvasElement | null> {
        if (!this._videoSink) return null;

        const clipLocalT = this.CompositionTimeToClipTime(compTimeSec);

        // If we haven't created an iterator yet (or after seek), create a fresh one
        if (!this._videoIter) {
            let iter: AsyncGenerator<WrappedCanvas, void, unknown>;
            try {
                iter = this._videoSink.canvases(clipLocalT);
                this._videoIter = iter;
            } catch (e) {
                this.handleVideoDecodeError(e);
                return null;
            }

            // prime by reading two frames like demo did:
            let first: WrappedCanvas | null = null;
            let second: WrappedCanvas | null = null;
            try {
                const r1 = await iter.next();
                if (!r1.done) first = r1.value as WrappedCanvas;
                const r2 = await iter.next();
                if (!r2.done) second = r2.value as WrappedCanvas;
            } catch (e) {
                if (this._videoIter === iter) {
                    this.handleVideoDecodeError(e);
                }
                try { (first as any)?.close?.(); } catch {}
                try { (second as any)?.close?.(); } catch {}
                return null;
            }
            if (this._videoIter !== iter) {
                try { (first as any)?.close?.(); } catch {}
                try { (second as any)?.close?.(); } catch {}
                return null;
            }
            this._nextFrame = (second ?? null) as WrappedCanvas | null;
            if (first) {
                const out = this.toHTMLCanvas((first as WrappedCanvas).canvas);
                try { (first as any)?.close?.(); } catch {}
                return out;
            }
        }

        // If there's a queued frame and it's due, emit it and queue next
        if (this._nextFrame && this._nextFrame.timestamp <= clipLocalT) {
            const current = this._nextFrame;
            const outCanvas = this.toHTMLCanvas(current.canvas);
            this._nextFrame = null;
            // queue a future one in background
            void this._updateNextFrame(compTimeSec);
            try { (current as any)?.close?.(); } catch {}
            return outCanvas;
        }

        // If no future frame queued yet, kick update
        if (!this._nextFrame) {
            void this._updateNextFrame(compTimeSec);
        }

        return null;
    }

    /**
     * Set crop for this video clip.
     * Crop values are normalized (0-1) relative to source dimensions.
     * x=0, y=0, width=1, height=1 means no crop (full frame visible).
     * When crop is applied, the clip's visual dimensions are updated proportionally.
     */
    public setCrop(crop: { x: number; y: number; width: number; height: number } | undefined): void {
        // Store original dimensions if not already set (only on FIRST crop)
        if (crop && (!this._originalSourceWidth || !this._originalSourceHeight)) {
            // Use current visual dimensions as the original reference
            // Prefer _width/_height if set, otherwise use canvas dimensions
            const currentW = typeof this._width === 'number' ? this._width : this.canvas.width || 1920;
            const currentH = typeof this._height === 'number' ? this._height : this.canvas.height || 1080;
            this._originalSourceWidth = currentW;
            this._originalSourceHeight = currentH;
        }
        
        this._crop = crop;
        
        // Invalidate video iterator to force fresh frame decode on next update
        // This ensures the new crop settings are applied immediately
        try { (this._videoIter as any)?.return?.(); } catch {}
        this._videoIter = undefined;
        try { (this._nextFrame as any)?.close?.(); } catch {}
        this._nextFrame = null;
        
        // Update visual dimensions based on crop
        if (crop && this._originalSourceWidth && this._originalSourceHeight) {
            // New dimensions = original * crop percentage
            const newW = Math.round(this._originalSourceWidth * crop.width);
            const newH = Math.round(this._originalSourceHeight * crop.height);
            this._width = newW;
            this._height = newH;
        } else if (!crop && this._originalSourceWidth && this._originalSourceHeight) {
            // Reset to original dimensions
            this._width = this._originalSourceWidth;
            this._height = this._originalSourceHeight;
            // Force canvas to resize on next frame by resetting the cached dimensions
            this._lastW = 0;
            this._lastH = 0;
        }
        
        this.trigger('frame', undefined);
    }

    /**
     * Get crop for this video clip.
     * Always returns a valid crop config (defaults to full video if not cropped).
     */
    public getCrop(): { x: number; y: number; width: number; height: number } {
        return this._crop;
    }

    /**
     * Get the original source dimensions (before any crop).
     * This is used to allow expanding back to full size when re-cropping.
     */
    public getOriginalSourceDimensions(): { width: number; height: number } | undefined {
        // Return stored original dimensions (set when first crop is applied)
        if (this._originalSourceWidth && this._originalSourceHeight) {
            return { width: this._originalSourceWidth, height: this._originalSourceHeight };
        }
        
        // If no crop has been applied yet, current dimensions ARE the original
        // Don't use _width/_height here as they might be the cropped size
        // Use canvas dimensions from decoded video
        const w = this.canvas.width;
        const h = this.canvas.height;
        if (w && h && w > 0 && h > 0) {
            return { width: w, height: h };
        }
        return undefined;
    }

    /**
     * Called every render tick by Track.update() → Composition.computeFrameAtTime().
     * Responsible for uploading the latest frame into this clip's display texture.
     */
    @visualize
    public async update(time: Timestamp): Promise<void> {
        // Apply optional shape mask (circle, etc.). Uses per-scene overrides when present.
        try {
            const sceneKey = ((this.track?.composition as any)?.currentSceneKey as string | undefined) ?? "__start__";
            const w = Number(this.track?.composition?.width ?? 1920) || 1920;
            const h = Number(this.track?.composition?.height ?? 1080) || 1080;
            const ratio = h > 0 ? w / h : 16 / 9;
            const aspect = Math.abs(ratio - 1) < 0.06 ? "square" : ratio < 1 ? "vertical" : "horizontal";
            const ov =
                ((this as any)?.sceneVisualOverridesByAspect &&
                    typeof (this as any).sceneVisualOverridesByAspect === "object" &&
                    (this as any).sceneVisualOverridesByAspect[aspect] &&
                    typeof (this as any).sceneVisualOverridesByAspect[aspect] === "object" &&
                    (this as any).sceneVisualOverridesByAspect[aspect][sceneKey]) ||
                (this as any)?.sceneVisualOverrides?.[sceneKey];
            const shape: MaskShape =
                typeof ov?.maskShape === "string"
                    ? (ov.maskShape as MaskShape)
                    : ((this as any)?.maskShape as MaskShape) ?? "none";
            applyShapeMask(this.view as any, shape);
        } catch {}

        // Apply visual fade-in/out transitions (video/image) by modulating alpha.
        try {
            const mul = this.getVisualFadeMultiplier(time.seconds);
            if (Number.isFinite(mul) && mul !== 1) {
                this.view.alpha = this.view.alpha * mul;
            }
        } catch {}

        // VIDEO PATH
        if (this._videoSink) {
            let maybeCanvas: HTMLCanvasElement | null = null;
            try {
                maybeCanvas = await this.pullVideoFrame(time.seconds);
            } catch (e) {
                this.handleVideoDecodeError(e);
                maybeCanvas = null;
            }
            if (maybeCanvas) {
                this._nextFrameCanvas = maybeCanvas as HTMLCanvasElement;

                // upload into our visual canvas/texture
                const src = this._nextFrameCanvas;
                if (src) {
                    // Store original source dimensions if not set
                    if (!this._originalSourceWidth || !this._originalSourceHeight) {
                        this._originalSourceWidth = src.width;
                        this._originalSourceHeight = src.height;
                    }
                    
	                    // Apply crop if configured (crop is normalized 0-1 values).
	                    // Scene/aspect overrides are computed in the @visualize decorator and passed via __librettoEffectiveCrop.
	                    const crop = ((this as any).__librettoEffectiveCrop as any) ?? this._crop;
	                    if (crop && (crop.x !== 0 || crop.y !== 0 || crop.width !== 1 || crop.height !== 1)) {
	                        // Calculate source rectangle from crop (in source pixels)
	                        const sx = crop.x * src.width;
	                        const sy = crop.y * src.height;
                        const sw = crop.width * src.width;
                        const sh = crop.height * src.height;
                        
                        // Canvas size = cropped dimensions (NOT stretched to original size)
                        const croppedW = Math.round(sw);
                        const croppedH = Math.round(sh);
                        
                        if (croppedW !== this._lastW || croppedH !== this._lastH) {
                            this._lastW = croppedW;
                            this._lastH = croppedH;
                            this.canvas.width = croppedW;
                            this.canvas.height = croppedH;
                        }
                        
                        // Draw cropped region at 1:1 scale (no stretching)
                        this.context.drawImage(src, sx, sy, sw, sh, 0, 0, croppedW, croppedH);
                    } else {
                        // No crop - draw full source
                        if (src.width !== this._lastW || src.height !== this._lastH) {
                            this._lastW = src.width;
                            this._lastH = src.height;
                            this.canvas.width = src.width;
                            this.canvas.height = src.height;
                        }
                        this.context.drawImage(src, 0, 0);
                    }
                    // notify renderer that the texture changed
                    this.textures.canvas.source.update();
                }
            }
        }
    }

    /**
     * Helper for mixer.registerTrack() clipId.
     * You need something stable per clip instance. You might already have `id`.
     */
    private getClipId(): string {
        // If MediaClip already has an id field, return it.
        // Otherwise generate once and cache.
        if ((this as any)._clipId) return (this as any)._clipId;
        (this as any)._clipId = crypto.randomUUID
            ? crypto.randomUUID()
            : `clip_${Date.now()}_${Math.random()}`;
        return (this as any)._clipId;
    }

    public onSeek(newCompTimeSec: number): void {
        // reset video iteration state so pullVideoFrame() restarts from new time
        try { (this._videoIter as any)?.return?.(); } catch {}
        this._videoIter = undefined;
        try { (this._nextFrame as any)?.close?.(); } catch {}
        this._nextFrame = null;
        this._nextFrameCanvas = undefined;
        // (audio side is handled separately in primeAudio())
    }

    /**
     * Compute a multiplier (0..1) for visual fade-in/out at clip edges.
     * Uses source-time internally so durations remain correct under playbackRate.
     */
    private getVisualFadeMultiplier(compTimeSec: number): number {
        const fadeIn = (this as any).visualFadeIn as any;
        const fadeOut = (this as any).visualFadeOut as any;
        const hasIn = fadeIn && typeof fadeIn.durationMs === "number" && fadeIn.durationMs > 0 && fadeIn.type !== "none";
        const hasOut = fadeOut && typeof fadeOut.durationMs === "number" && fadeOut.durationMs > 0 && fadeOut.type !== "none";
        if (!hasIn && !hasOut) return 1;

        const clipStartSrcSec = this.range?.[0]?.seconds ?? 0;
        const clipEndSrcSec = this.range?.[1]?.seconds ?? clipStartSrcSec;
        if (!Number.isFinite(clipStartSrcSec) || !Number.isFinite(clipEndSrcSec) || clipEndSrcSec <= clipStartSrcSec) return 1;

        const rate = Number.isFinite(this.playbackRate) && this.playbackRate > 0 ? this.playbackRate : 1;
        const tSrc = this.CompositionTimeToClipTime(compTimeSec);
        if (!Number.isFinite(tSrc)) return 1;

        let mul = 1;

        if (hasIn) {
            const durSrc = (Number(fadeIn.durationMs ?? 0) / 1000) * rate;
            if (durSrc > 0) {
                const endSrc = clipStartSrcSec + durSrc;
                if (tSrc >= clipStartSrcSec && tSrc < endSrc) {
                    const t = Math.max(0, Math.min(1, (tSrc - clipStartSrcSec) / durSrc));
                    mul *= Math.sin(t * HALF_PI);
                }
            }
        }

        if (hasOut) {
            const durSrc = (Number(fadeOut.durationMs ?? 0) / 1000) * rate;
            if (durSrc > 0) {
                const startSrc = clipEndSrcSec - durSrc;
                if (tSrc >= startSrc && tSrc < clipEndSrcSec) {
                    const t = Math.max(0, Math.min(1, (tSrc - startSrc) / durSrc));
                    mul *= Math.cos(t * HALF_PI);
                }
            }
        }

        return mul;
    }

    /**
     * Get whether Studio Sound (denoised audio) is enabled.
     */
    public get studioSoundEnabled(): boolean {
        return this._studioSoundEnabled;
    }

    /**
     * Set Studio Sound state and swap audio source accordingly.
     * @param enabled - Whether to use denoised audio
     * @param denoisedUrl - URL for denoised audio (required when enabling)
     * @param originalUrl - URL for original audio (required when disabling, or stored from first enable)
     */
    public async setStudioSound(enabled: boolean, denoisedUrl?: string, originalUrl?: string): Promise<void> {
        if (enabled === this._studioSoundEnabled) {
            return;
        }

        // Store URLs for future swapping
        if (originalUrl) this._originalAudioUrl = originalUrl;
        if (denoisedUrl) this._denoisedAudioUrl = denoisedUrl;

        if (enabled) {
            // Enable Studio Sound - use denoised audio
            const targetUrl = this._denoisedAudioUrl;
            if (!targetUrl) {
                console.warn('[VideoClip.setStudioSound] Missing denoised audio URL');
                return;
            }
            await this.replaceAudioSource(targetUrl);
        } else {
            // Disable Studio Sound - revert to original video's audio track
            await this.revertToOriginalAudio();
        }

        this._studioSoundEnabled = enabled;

        // Emit event for UI to update waveform
        this.trigger('studioSoundChanged', { enabled, clipId: this.id });
    }

    /**
     * Revert to the original audio track from the video source.
     */
    private async revertToOriginalAudio(): Promise<void> {
        // Stop current audio playback
        this.stopAudio();

        // Clear custom audio input
        this._customAudioInput = undefined;

        // Reinitialize audio sink from original video source
        try {
            const input = this.videoSource.input;
            const audioTrack = await input.getPrimaryAudioTrack();
            if (audioTrack && (await audioTrack.canDecode())) {
                const newSampleRate = audioTrack.sampleRate;
                
                // Check if sample rate changed - need to clear mixer track for re-registration
                const sampleRateChanged = this._sample_rate !== newSampleRate;
                
                this._audioSink = new AudioBufferSink(audioTrack);
                this._sample_rate = newSampleRate;
                this.hasAudioStream = true;
                
                // Clear mixer track if sample rate changed so it re-registers with correct rate
                if (this._mixerTrack && sampleRateChanged) {
                    console.log('[VideoClip.revertToOriginalAudio] Sample rate changed back to', newSampleRate, '- clearing mixer track');
                    this._mixerTrack = undefined;
                }
            }

            // Emit event for UI
            this.trigger('sourceChanged', { clipId: this.id });
        } catch (err) {
            console.error('[VideoClip.revertToOriginalAudio] Failed:', err);
            throw err;
        }
    }

    /**
     * Replace the audio source with a new URL while keeping video unchanged.
     * Used for Studio Sound (denoised audio) switching.
     * @param newAudioUrl - The new audio URL to load
     */
    public async replaceAudioSource(newAudioUrl: string): Promise<void> {
        // 1. Stop current audio playback
        this.stopAudio();

        // 2. Create new Input from URL for audio only
        const newAudioInput = new Input({
            formats: ALL_FORMATS,
            source: new UrlSource(newAudioUrl),
        });

        // Store for potential reuse
        this._customAudioInput = newAudioInput;

        // 3. Reinitialize audio sink with new source
        try {
            const audioTrack = await newAudioInput.getPrimaryAudioTrack();
            if (!audioTrack) {
                throw new Error('New audio source has no audio track');
            }
            if (!(await audioTrack.canDecode())) {
                throw new Error('Cannot decode new audio source');
            }

            this._audioSink = new AudioBufferSink(audioTrack);
            const newSampleRate = audioTrack.sampleRate;
            
            // Check if sample rate changed - need to clear mixer track for re-registration
            const sampleRateChanged = this._sample_rate !== newSampleRate;
            const oldSampleRate = this._sample_rate;
            
            this._sample_rate = newSampleRate;
            this.hasAudioStream = true;

            // 4. Clear mixer track if sample rate changed
            // The mixer worklet uses the registered sample rate to convert time→sample index
            // Wrong rate = wrong samples = pitch shift (e.g., 44100Hz played at 48000Hz = helium voice)
            // Clearing it forces re-registration with correct rate on next prepareForPlayback()
            if (this._mixerTrack && sampleRateChanged) {
                console.log('[VideoClip.replaceAudioSource] Sample rate changed from', 
                    oldSampleRate, 'to', newSampleRate, '- clearing mixer track for re-registration');
                this._mixerTrack = undefined;
            }

            // 5. Emit event for UI to know source changed
            this.trigger('sourceChanged', { clipId: this.id });
            
            console.log('[VideoClip.replaceAudioSource] Audio replaced, sampleRate:', newSampleRate);
        } catch (err) {
            console.error('[VideoClip.replaceAudioSource] Failed to replace audio source:', err);
            throw err;
        }
    }

    /**
     * Create a deep clone of this video clip with all properties.
     * The clone gets a new ID but shares the same source.
     */
    public clone(): VideoClip {
        const cloned = new VideoClip(this.videoSource, { ...this.props }, undefined);

        // Copy timing properties
        cloned._offset = Timestamp.fromSeconds(this._offset.seconds);
        cloned.duration.seconds = this.duration.seconds;
        cloned.range = [
            Timestamp.fromSeconds(this.range[0].seconds),
            Timestamp.fromSeconds(this.range[1].seconds)
        ];
        cloned._playbackRate = this._playbackRate;
        cloned.gain = this.gain;

        // Copy visual properties (from VisualMixin)
        cloned._position = { ...this._position };
        cloned._width = this._width;
        cloned._height = this._height;
        cloned.alpha = this.alpha;
        cloned.rotation = this.rotation;
        if (this._scale) cloned._scale = { ...this._scale };
        cloned.translate = { ...this.translate };

        // Copy cuts (deep clone with new IDs)
        cloned.cuts = this.cuts.map(c => ({
            id: crypto?.randomUUID?.() ?? `cut_${Date.now()}_${Math.random()}`,
            startSec: c.startSec,
            endSec: c.endSec,
            groupId: c.groupId
        }));
        
        // Copy fade configurations
        if (this.fadeIn) {
            cloned.fadeIn = { ...this.fadeIn };
        }
        if (this.fadeOut) {
            cloned.fadeOut = { ...this.fadeOut };
        }

        // Copy visual fade configurations
        if (this.visualFadeIn) {
            cloned.visualFadeIn = { ...this.visualFadeIn };
        }
        if (this.visualFadeOut) {
            cloned.visualFadeOut = { ...this.visualFadeOut };
        }
        
        // Copy transition points (deep clone)
        cloned.transitionPoints = this.transitionPoints.map(tp => ({
            id: crypto?.randomUUID?.() ?? `tp_${Date.now()}_${Math.random()}`,
            srcTimeSec: tp.srcTimeSec,
            transition: tp.transition ? {
                fadeOut: tp.transition.fadeOut ? { ...tp.transition.fadeOut } : undefined,
                fadeIn: tp.transition.fadeIn ? { ...tp.transition.fadeIn } : undefined,
            } : undefined,
        }));

        // Copy name if set
        if (this.name) cloned.name = this.name;

        // Normalize and derive segments
        cloned.normalizeCuts();
        cloned.deriveSegments();

        return cloned;
    }
}
